import type {enUS} from '@clerk/localizations';

type ClerkLocalization = typeof enUS;

/**
 * Sign-in failures Clerk reports with a message that reveals whether the
 * submitted identifier belongs to an account (SVM-11490).
 *
 * `form_identifier_not_found` ("Couldn't find your account.") is only ever
 * returned for an unknown identifier, and `form_password_incorrect`
 * ("Password is incorrect...") only for a known one — so the pair lets anyone
 * enumerate registered users by reading the error text alone.
 */
const ENUMERATING_ERROR_KEYS = [
  'form_identifier_not_found',
  'form_password_incorrect',
  'form_password_or_identifier_incorrect',
] as const;

/**
 * Collapse the enumeration-revealing sign-in errors of `base` onto a single
 * generic message, leaving every other string untouched.
 *
 * Applied at the `ClerkProvider` so it also covers the password-reset and
 * account-recovery flows, which surface the same identifier lookup.
 */
export function withGenericAuthErrors(
  base: ClerkLocalization | undefined,
  message: string,
): ClerkLocalization {
  const genericErrors = Object.fromEntries(
    ENUMERATING_ERROR_KEYS.map((key) => [key, message]),
  );

  return {
    ...base,
    unstable__errors: {
      ...base?.unstable__errors,
      ...genericErrors,
    },
  };
}
